import React, { useState } from "react";
import cx from "clsx";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { LayoutSettings } from "../../../../boxes/LayoutSettings/LayoutSettings";
import { EntityHeader } from "../../../../widgets/EntityHeader/EntityHeader";
import styles from "./scss/PatientFi.module.scss";
import { Toggle } from "../../../../shared/Toggle/Toggle";
import { useAppTranslation } from "../../../../i18n/useAppTranslation";
import RegisterContainer from "./components/RegisterContainer";
import { usePatientFiIntegration } from "./hooks/usePatientFiIntegration";
import { useSetPatientFiIntegration } from "./hooks/useSetPatientFiIntegration";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";
import { QUERY_KEYS } from "../../../../consts/api";
import ConfiguredClinics from "./components/ConfiguredClinics/ConfiguredClinics";
import { getIsAcceleratorPlan } from "../../../../Utils";
import UpgradeAccountModal from "../../../../Components/Common/UpgradeAccountModal";
import { uiNotification } from "../../../../services/UINotificationService";

const PatientFi = () => {
  const { tSettings } = useAppTranslation.Settings();

  const queryClient = useQueryClient();
  const { data, isLoading } = usePatientFiIntegration({
    enabled: getIsAcceleratorPlan(),
  });
  const { mutate, isLoading: isMutating } = useSetPatientFiIntegration();
  const [showUpgradeModal, setShowUpgradeModal] = useState(
    !getIsAcceleratorPlan(),
  );

  const { values } = useFormik({
    initialValues: {
      isActive: data?.isActive || false,
    },
    enableReinitialize: true,
  });

  const handleToggle = () => {
    mutate(
      {
        isActive: !values.isActive,
      },
      {
        onSuccess: (data) => {
          const successMessages = data?.data?.data?.messages;
          const errors = data?.data?.data?.errors;
          if (successMessages.length > 0) {
            successMessages.forEach((message) => {
              uiNotification.success(message);
            });
          }

          if (errors.length > 0) {
            errors.forEach((error) => {
              uiNotification.error(error);
            });
          }
          queryClient.setQueryData(
            [QUERY_KEYS.patientFi.getIntegration],
            () => data,
          );
        },
      },
    );
  };

  return (
    <LayoutSettings contentClassName={styles.root}>
      <EntityHeader
        title={tSettings("patientFiIntegration.title")}
        right={
          <div className={cx(!getIsAcceleratorPlan() && "cursor-not-allowed")}>
            {isMutating ? (
              <CircularProgress size="small" />
            ) : (
              <Toggle
                isEnabled={values.isActive}
                onChange={handleToggle}
                isDisabled={isLoading || !getIsAcceleratorPlan()}
              />
            )}
          </div>
        }
      />
      {values.isActive && (
        <>
          <RegisterContainer links={data?.links} />
          <ConfiguredClinics />
        </>
      )}
      {!getIsAcceleratorPlan() && (
        <UpgradeAccountModal
          notificaionType={"integration"}
          isShowUpgradeAlertModal={showUpgradeModal}
          handleShowUpgradeAlertModal={() =>
            setShowUpgradeModal(!showUpgradeModal)
          }
        />
      )}
    </LayoutSettings>
  );
};

export default PatientFi;
